import { Box, Stack, Typography, useTheme } from "@mui/material";
import { AlertCircle } from "lucide-react";
import type { FC } from "react";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { formatRate } from "web_myplann/Goals/Hooks/useLoanCalculator";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { LabeledValueRow } from "web_myplann/Shared/Components/LabeledValueRow";

type Props = {
	rate: number;
	termYears: number;
	monthlyPayment: number;
	totalPaid: number;
	overpay: number;
};

export const LoanCalculatorSummary: FC<Props> = ({
	rate,
	termYears,
	monthlyPayment,
	totalPaid,
	overpay,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	return (
		<Stack px={1.6} py={1.5} gap={0.6}>
			<LabeledValueRow
				label={
					<Typography variant="bodyStrong">
						{t(Resources.Goals.Detail.LoanCalculator.monthlyPayment)}
					</Typography>
				}
				value={
					<Typography variant="subtitle3" tabular>
						{t(Resources.Goals.Detail.LoanCalculator.amountValue, {
							amount: monthlyPayment.toLocaleString("cs-CZ"),
						})}
					</Typography>
				}
			/>
			<Typography variant="caption" color={theme.palette.grey[500]} tabular>
				{t(Resources.Goals.Detail.LoanCalculator.monthlyMeta, {
					rate: formatRate(rate),
					term: t(Resources.Goals.Detail.LoanCalculator.termValue, {
						count: termYears,
					}),
				})}
			</Typography>
			<LabeledValueRow
				label={
					<Typography variant="caption" color={theme.palette.grey[500]}>
						{t(Resources.Goals.Detail.LoanCalculator.totalPaid)}
					</Typography>
				}
				value={
					<Typography variant="caption" color={theme.palette.grey[500]} tabular>
						{t(Resources.Goals.Detail.LoanCalculator.amountValue, {
							amount: formatAmount(totalPaid),
						})}
					</Typography>
				}
			/>
			<LabeledValueRow
				label={
					<Typography variant="caption" color={theme.palette.grey[500]}>
						{t(Resources.Goals.Detail.LoanCalculator.interest)}
					</Typography>
				}
				value={
					<Typography
						variant="caption"
						color={theme.palette.amber[600]}
						tabular
					>
						{t(Resources.Goals.Detail.LoanCalculator.interestValue, {
							amount: formatAmount(overpay),
						})}
					</Typography>
				}
			/>
			<Stack
				direction="row"
				alignItems="flex-start"
				gap={0.8}
				mt={0.8}
				pt={1}
				borderTop={1}
				borderColor="surface.border"
			>
				<Box mt={0.3} flexShrink={0}>
					<AlertCircle size={12} color={theme.palette.grey[400]} />
				</Box>
				<Typography
					variant="caption"
					color={theme.palette.grey[500]}
					lineHeight={1.5}
				>
					{t(Resources.Goals.Detail.LoanCalculator.disclaimer)}
				</Typography>
			</Stack>
		</Stack>
	);
};
